import _ from 'lodash';
import { setHARIntoErrorState } from '../helpers/httpErrorHarConverter.js';
class ApiService {
  token;

  static async refreshToken() {
    const token = await window.DocgoAuth.getAccessToken();
    this.token = `Bearer ${token}`;
  }

  static async makeRequest(url, method, body, parameters = {}) {
    await this.refreshToken();

    const headers = {
      'x-ambulnz-web': true,
      'Content-Type': 'application/json',
    };

    if (parameters.noCache) {
      Object.assign(headers, {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
        'If-Modified-Since': 0,
      });
    }

    if (this.token) {
      headers.Authorization = this.token;
    }

    const params = { method, headers };

    if (body) {
      params.body = JSON.stringify(body);
    }

    return fetch(url, params).then(async response => {
      if (response.status >= 400 && response.status < 600) {
        setHARIntoErrorState(response, params, url);
      }
      return response;
    });
  }

  /**
   * Makes an HTTP request with proper error handling.
   *
   * @param {string} url - The URL to request.
   * @param {Object} [options={}] - Request options.
   * @param {string} [options.method='GET'] - HTTP method.
   * @param {Object} [options.body] - Request body, will be JSON serialized.
   * @param {'json'|'text'|'response'} [options.responseType='json'] - Expected response type.
   * @param {boolean} [options.noCache=false] - If true, adds cache-busting headers.
   *
   * @returns {Promise<Object|string|Blob|null>} Parsed response body.
   *
   * @throws {NetworkError} When the request fails due to network issues (no connection, CORS, DNS).
   * @throws {HttpError} When the server responds with a 4xx or 5xx status code.
   * @throws {ParseError} When the response body cannot be parsed as JSON.
   *
   * @example
   * // GET request
   * const data = await makeRequestSafe('/api/users');
   *
   * @example
   * // POST request
   * const data = await makeRequestSafe('/api/users', { method: 'POST', body: { name: 'John' } });
   *
   * @example
   * // Error handling
   * try {
   *   const data = await makeRequestSafe('/api/users');
   * } catch (err) {
   *   if (err instanceof HttpError && err.status === 401) // handle auth
   *   if (err instanceof NetworkError) // handle offline
   *   if (err instanceof ParseError) // handle bad response
   * }
   */
  static async makeRequestSafe(url, { method = 'GET', body, responseType = 'json', ...restOptions } = {}) {
    const meta = { url, method };

    let response;
    try {
      response = await this.makeRequest(url, method, body, restOptions);
    } catch (err) {
      throw new NetworkError(err.message, meta);
    }

    if (responseType === 'response') return response;

    const responseMeta = { ...meta, xRequestId: requestIdHeader(response) };

    if (!response.ok) {
      const rawBody = await response.text().catch(() => '');
      const parsedBody = parseHttpErrorBody(rawBody);

      throw new HttpError({
        status: response.status,
        message: getHttpErrorMessage(parsedBody, rawBody, response.status),
        body: parsedBody,
        rawBody: parsedBody ? undefined : rawBody,
        ...responseMeta,
      });
    }

    if (responseType === 'text') return response.text();

    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (err) {
      throw new ParseError(err.message, text, responseMeta);
    }
  }

  static get(url, payload, parameters = {}) {
    if (payload) {
      url += this.objectToURLQueryString(payload);
    }

    return this.makeRequest(url, 'GET', null, parameters);
  }

  static post(url, data) {
    return this.makeRequest(url, 'POST', data);
  }

  static delete(url) {
    return this.makeRequest(url, 'DELETE');
  }

  static put(url, data) {
    return this.makeRequest(url, 'PUT', data);
  }

  static patch(url, data) {
    return this.makeRequest(url, 'PATCH', data);
  }

  static async postFile(url, data) {
    await this.refreshToken();

    const headers = {
      Authorization: this.token,
    };

    if (data.type) {
      headers['Content-Type'] = data.type;
    }

    return fetch(url, {
      method: 'POST',
      body: data,
      headers,
    });
  }

  static objectToURLQueryString(obj) {
    return Object.entries(obj)
      .map(([key, val]) => {
        if (_.isPlainObject(val)) {
          return Object.entries(val)
            .map(([nkey, nval]) => `${key}[${nkey}]=${encodeURIComponent(nval)}`)
            .join('&');
        }
        return `${key}=${encodeURIComponent(val)}`;
      })
      .join('&');
  }
}

const requestIdHeader = response => response.headers?.get('X-Request-ID');

const parseHttpErrorBody = rawBody => {
  if (!rawBody) {
    return null;
  }

  try {
    return JSON.parse(rawBody);
  } catch {
    return null;
  }
};

const getHttpErrorMessage = (body, rawBody, status) =>
  body?.error?.message ||
  body?.message ||
  (typeof body?.error === 'string' ? body.error : null) ||
  rawBody ||
  `Request failed with status ${status}`;

export class NetworkError extends Error {
  constructor(message, { url, method } = {}) {
    super(`Network error: ${message}`);
    this.name = 'NetworkError';
    this.url = url;
    this.method = method;
  }
}

export class HttpError extends Error {
  constructor({ status, message, body, rawBody, url, method, xRequestId } = {}) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.body = body;
    this.rawBody = rawBody;
    this.url = url;
    this.method = method;
    this.xRequestId = xRequestId;
  }
}

export class ParseError extends Error {
  constructor(message, rawBody, { url, method, xRequestId } = {}) {
    super(`Parse error: ${message}`);
    this.name = 'ParseError';
    this.rawBody = rawBody;
    this.url = url;
    this.method = method;
    this.xRequestId = xRequestId;
  }
}

export default ApiService;
